// Avatar color constants
export const AVATAR_COLORS = [
    '#9E0000', // Red
    '#D68400', // Orange
    '#408F00', // Green
    '#87B45E', // Light Green
    '#A347B6', // Magenta
    '#5847B6', // Violet
    '#0052AB', // Blue
    '#00A38C', // Teal
    '#006557', // Forest Green
    '#BB83AC', // Pink
    '#907A45', // Gold
    '#839ABB', // Dusty Blue
    '#D87373', // Coral
    '#A45500'  // Ember
];

export const CURRENT_USER_COLOR = '#006A99'; // Blue for current user

/**
 * Get user initials from full name
 * @param name - Full name string
 * @returns Initials string (e.g., "JD" for "John Doe")
 */
export const getInitials = (name: string): string => {
    if (!name) return '';
    
    const names = name.split(' ');
    if (names.length === 1) return names[0].charAt(0).toUpperCase();
    
    return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
};

/**
 * Get avatar color for a user based on their ID
 * @param userId - User ID (number or string)
 * @param currentUserId - Current user's ID for special highlighting
 * @returns Hex color string
 */
export const getUserColor = (userId: number | string, currentUserId: number | string): string => {
    if (userId === currentUserId) return CURRENT_USER_COLOR;
    
    // Convert userId to a number for consistent modulo operation
    const numericId = typeof userId === 'string' ? userId.charCodeAt(0) : userId;
    return AVATAR_COLORS[Math.abs(numericId) % AVATAR_COLORS.length];
};

/**
 * Determine task status based on date and completion state
 * @param dateString - Task date string
 * @param task - Optional task object to check completion status
 * @returns Status object with label and color
 */
export const getTaskStatus = (dateString: string, task?: any): { label: string, color: string } => {
    // If task is completed, return green status
    if (task?.isCompleted === 1) {
        return { label: 'Completed', color: 'success' };
    }

    const dateToUse = dateString;
    const taskDate = new Date(dateToUse);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const timeDiff = taskDate.getTime() - today.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
    
    if (daysDiff < -1) {
        // Date passed - Red
        return { label: `${Math.abs(daysDiff)} days ago`, color: 'danger' };
    } else if (daysDiff === -1) {
        // Yesterday - Red
        return { label: '1 day ago', color: 'danger' };
    } else if (daysDiff === 0) {
        // Today - Yellow
        return { label: 'Today', color: 'warning' };
    } else if (daysDiff === 1) {
        // Tomorrow - Yellow
        return { label: 'In 1 day', color: 'warning' };
    } else if (daysDiff <= 7) {
        // 2-7 calendar days - Yellow
        return { label: `In ${daysDiff} days`, color: 'warning' };
    } else {
        // More than 7 days - Blue
        return { label: `In ${daysDiff} days`, color: 'info' };
    }
};

/**
 * Build the updated in-file task list after a task is removed (deactivated) from a file.
 *
 * @param tasks - Current persisted in-file tasks
 * @param task - The task being removed
 * @returns A new tasks array with the task deactivated (never mutates the input)
 */
export const applyTaskRemoval = (tasks: any[], task: any): any[] => {
    // Deactivate the task. isCompleted: 1 keeps the removal sticky: modifyExistingTask
    // skips re-activation for completed tasks, so a profile-derived task stays removed
    // across re-renders instead of being regenerated as active.
    const deactivate = (t: any) => ({
        ...t,
        isCompleted: 1,
        isOverwriteActivate: 1,
        isActivated: 0,
    });

    const taskIndex = tasks.findIndex(t => t.typeID === task.typeID);

    // Task not yet persisted in state (e.g. an untouched, unassigned profile task):
    // append it as a deactivated entry so the removal is not silently dropped.
    if (taskIndex === -1) {
        return [...tasks, deactivate(task)];
    }

    return tasks.map((t, i) => (i === taskIndex ? deactivate(t) : t));
};

/**
 * Extract meaningful content from task title using nested parenthesis logic
 * @param fullTitle - Full task title string
 * @returns Parsed title string
 */
export const parseTaskTitle = (fullTitle: string): string => {
    // Check for nested parentheses pattern
    const nestedParenthesesRegex = /\(([^()]*\([^()]*\)[^()]*)\)/;
    const nestedMatches = fullTitle.match(nestedParenthesesRegex);
    
    if (nestedMatches) {
        // We found nested parentheses, return the outer group content
        return nestedMatches[1];
    } else {
        // No nested parentheses, try to get second parenthesis group
        const secondBracketRegex = /.*\(([^)]*)\)(?!.*\([^)]*\))/;
        const matches = fullTitle.match(secondBracketRegex);
        return matches ? matches[1] : fullTitle;
    }
}; 